import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '../../auth/AuthProvider';
import {
  buildTripManagementMagicPath,
  isMagicLinkDeactivated,
  setLoginEmailPrefill,
} from '../../utils/magicLink';

const ClaimTrip = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  const magic = searchParams.get('magic')?.trim() || '';
  const emailFromLink = searchParams.get('email')?.trim() || '';
  const [email, setEmail] = useState(emailFromLink);

  const deactivated = useMemo(
    () => (magic ? isMagicLinkDeactivated(magic) : false),
    [magic],
  );

  const tripPath = magic
    ? buildTripManagementMagicPath(magic)
    : '/app/tripmanagement';

  useEffect(() => {
    if (isLoading || !magic || deactivated) {
      return;
    }
    if (isAuthenticated) {
      navigate(tripPath, { replace: true });
    }
  }, [isAuthenticated, isLoading, magic, deactivated, navigate, tripPath]);

  const handleContinue = (event: FormEvent) => {
    event.preventDefault();
    if (!magic) {
      return;
    }
    localStorage.setItem('redirectAfterLogin', tripPath);
    if (email.trim()) {
      setLoginEmailPrefill(email.trim());
    }
    loginWithRedirect();
  };

  if (!magic) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center gap-4 px-6 py-12">
        <h1 className="text-2xl font-semibold text-neutral-800">
          Invitation link incomplete
        </h1>
        <p className="text-neutral-600">
          This invite is missing its access token. Ask your trip organizer to
          resend the invitation, or open the link from your email again.
        </p>
        <Link className="text-brand-santtai-primary underline" to="/">
          Back to home
        </Link>
      </div>
    );
  }

  if (deactivated) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center gap-4 px-6 py-12">
        <h1 className="text-2xl font-semibold text-neutral-800">
          Invitation no longer active
        </h1>
        <p className="text-neutral-600">
          This invite link was deactivated by the trip organizer. Ask them to
          send a new invitation if you still need access.
        </p>
        <Link className="text-brand-santtai-primary underline" to="/">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center gap-6 px-6 py-12">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-neutral-800">
          Join your dive trip
        </h1>
        <p className="text-neutral-600">
          Sign in or create an account to complete your guest details for this
          booking.
        </p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={handleContinue}>
        <label className="flex flex-col gap-2 text-sm text-neutral-700">
          Email for this invite
          <input
            type="email"
            className="rounded-lg border border-neutral-300 px-3 py-3 text-base text-neutral-800"
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            aria-label="Email for this invite"
          />
        </label>
        <button
          type="submit"
          className="rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker px-6 py-3 font-semibold text-neutral-00"
        >
          Continue to sign in
        </button>
      </form>

      <p className="text-sm text-neutral-500">
        Already signed in?{' '}
        <Link className="underline text-neutral-800" to={tripPath}>
          Open trip management
        </Link>
      </p>
    </div>
  );
};

export default ClaimTrip;
